import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AssociadoId {
  associadoId: string | null;
  /**
   * Rascunho de adesão em curso (at-38, item 22). Vive aqui — e não em slice
   * próprio — porque é o mesmo ciclo de vida do fluxo: `clearAssociadoData` já
   * o zera no logout e no login, sem precisar tocar em `authLogout.ts`.
   */
  rascunhoId: string | null;
}

interface Endereco {
  rua: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

interface Carro {
  /**
   * Motocicleta elétrica do catálogo (at-46, item 50). Preenchido = o veículo é uma
   * bike: a tela some com o FIPE, a placa é gerada pelo backend e o valor
   * protegido é a única base. Vai no payload do cadastro.
   */
  bikeEletricaId?: string | null;
  /**
   * Veículo 0km (at-81): sem placa e sem renavam. Vai no payload do cadastro; o
   * backend grava `0KM-<chassi>` e, depois do cadastro, `placa` passa a guardar essa
   * identificação provisória (é por ela que as etapas seguintes acham o veículo).
   */
  zeroKm?: boolean;
  placa: string;
  placaSubstituido?: string | null;
  codigoVeiculoSubstituido?: string | null;
  placaAntiga?: string | null;
  codigoVeiculoAntigo?: string | null;
  codigoAssociadoNovo?: string | null;
  anoModelo: number;
  anoFabricacao: number;
  /*
   * at-59: estas três SEMPRE foram gravadas aqui — as telas de veículo dispatcham
   * o `carroData` inteiro —, mas faltavam na interface, então quem lesse o slice
   * não as enxergava. Acrescentar é correção de TIPO, não de shape: nenhuma chave
   * nova passa a ser gravada e os rascunhos do banco continuam válidos.
   */
  marcaId?: string;
  cotaDescricao?: string;
  bikeMarcaId?: string;
  marcaNome: string;
  modelo: string;
  chassi: string;
  renavam: string;
  codigoFipe: string;
  valorProtegido: string;
  valorFipe: string;
  kmRodados?: number;
  corId: string;
  corNome: string;
  combustivelId: string;
  combustivelNome: string;
  cambioId: string;
  cambioNome: string,
  tipoVeiculoId: string;
  tipoVeiculoNome: string,
  categoriaId: string;
  categoriaNome: string;
  cooperativaId: string;
  cooperativaNome: string;
  cotaId: string;
  cotaNome: string;
  vencimentoDesejado: number;
  endereco: Endereco;
  valorAdesao?: string;
  /*
   * ⚠️ **É STRING em runtime**, apesar de o tipo dizer `number` desde sempre:
   * `SgaCota.valor_cota` é `Decimal` no banco e chega como `string | null` na API
   * (`api/sga-cota/sga-cotaApi.ts`) — e é por isso que `views/documento` faz
   * `parseFloat()` nele, o que só faz sentido para string.
   *
   * O `number` nunca deu erro porque a gravação escapava da checagem: as telas de
   * veículo enxertavam a chave num objeto intermediário, onde o TypeScript não
   * aplica excess property check. Com o `FormularioVeiculo` tipado (at-59), o furo
   * apareceu — e o tipo foi corrigido para a realidade, não o contrário.
   */
  valorCota?: string | number;
}

interface AssociadoFormData {
  nomeCompleto: string;
  cpfCnpj: string;
  rg: string;
  dataNascimento: string;
  sexo?: string;
  sexoId: string;
  email: string;
  dddCelular: string;
  celular: string;
  regionalId: string;
  endereco: Endereco;
  carro?: Carro;
  rua?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
}

const initialState: AssociadoFormData & AssociadoId = {
  associadoId: null,
  rascunhoId: null,
  nomeCompleto: '',
  cpfCnpj: '',
  rg: '',
  dataNascimento: '',
  sexoId: '',
  email: '',
  dddCelular: '',
  celular: '',
  regionalId: '',
  endereco: {
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
  },
  carro: undefined,
};

const associadoSlice = createSlice({
  name: 'associado',
  initialState,
  reducers: {
    updateAssociadoData(state, action: PayloadAction<Partial<AssociadoFormData>>) {
      return { ...state, ...action.payload };
    },
    addCarroData(state, action: PayloadAction<Partial<Carro>>) {
      state.carro = { ...(state.carro ?? {}), ...action.payload } as Carro;
    },
    setAssociadoId(state, action: PayloadAction<string>) {
      state.associadoId = action.payload;
    },
    /**
     * `null` quando o rascunho deixou de existir (concluído/descartado em outro
     * dispositivo — 409): o próximo passo do fluxo cria um novo.
     */
    setRascunhoId(state, action: PayloadAction<string | null>) {
      state.rascunhoId = action.payload;
    },
    updateValorAdesao(state, action: PayloadAction<string>) {
      if (state.carro) {
        state.carro.valorAdesao = action.payload;
      }
    },
    clearAssociadoData() {
      return initialState;
    },
  },
});

export const {
  updateAssociadoData,
  addCarroData,
  setAssociadoId,
  setRascunhoId,
  updateValorAdesao,
  clearAssociadoData,
} = associadoSlice.actions;
export default associadoSlice.reducer;
